import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import useSEO from '../hooks/useSEO';
import { useLanguage } from '../context/LanguageContext';
import AboutIntro from '../components/AboutIntro';
import AboutStory from '../components/AboutStory';
import AboutExpertise from '../components/AboutExpertise';

const { FiArrowLeft, FiArrowRight, FiMapPin, FiMail } = FiIcons;

const About = () => {
  const { lang } = useLanguage();
  const isSpanish = lang === 'es';
  const tidycalUrl =
    'https://tidycal.com/mathiasdeppe/free-15-minute-growth-review';

  useSEO(
    isSpanish
      ? 'Sobre Mathias Deppe | Consultor de Crecimiento Digital Cerca de Barcelona'
      : 'About Mathias Deppe | Digital Growth Consultant Near Barcelona',
    isSpanish
      ? 'Conoce a Mathias Deppe, consultor de crecimiento digital cerca de Barcelona, especializado en mensajes claros, contenido que genera confianza y sistemas de adquisición de clientes.'
      : 'Meet Mathias Deppe, a digital growth consultant based near Barcelona who builds clear messaging, trusted content and customer acquisition systems.'
  );

  return (
    <div className="bg-zinc-950 min-h-screen pt-32 pb-24 px-6 text-white">
      <main className="max-w-6xl mx-auto">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-orange-500 hover:text-orange-400 font-bold uppercase tracking-widest text-xs mb-14 transition-colors"
        >
          <SafeIcon icon={FiArrowLeft} />
          {isSpanish ? 'Volver al inicio' : 'Back to Home'}
        </Link>

        <AboutIntro />

        <section className="mb-28 p-8 md:p-14 bg-zinc-900/60 border border-white/5 rounded-[2.5rem]">
          <h2 className="text-4xl md:text-5xl font-black tracking-tighter leading-[0.95] mb-8">
            {isSpanish
              ? 'Por qué trabajo de forma diferente.'
              : 'Why I work differently.'}
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 text-lg leading-relaxed text-zinc-400">
            <div className="space-y-5">
              <p className="text-white font-bold">
                {isSpanish
                  ? 'La mayoría de agencias empieza con:'
                  : 'Most agencies start with:'}
              </p>

              <p className="text-2xl font-black text-orange-500">
                {isSpanish
                  ? '“¿Qué deberíamos publicar este mes?”'
                  : '“What should we post this month?”'}
              </p>

              <p className="text-white font-bold">
                {isSpanish
                  ? 'Yo empiezo en otro lugar:'
                  : 'I start somewhere else:'}
              </p>

              <p className="text-2xl font-black text-white">
                {isSpanish
                  ? '“¿Por qué debería comprarte alguien?”'
                  : '“Why should someone buy from you?”'}
              </p>
            </div>

            <div className="space-y-5">
              <p>
                {isSpanish
                  ? 'Un feed bonito de Instagram no arregla un posicionamiento confuso. Más seguidores no generan automáticamente más clientes. Y conseguir leads no sirve de nada si nadie hace seguimiento.'
                  : 'A beautiful Instagram feed does not fix unclear positioning. More followers do not automatically create more customers. And generating leads does not help if nobody follows up with them.'}
              </p>

              <p>
                {isSpanish
                  ? 'Por eso analizo todo el recorrido:'
                  : 'That is why I look at the entire customer journey:'}
              </p>

              <div className="space-y-3 py-2">
                {(isSpanish
                  ? [
                      'DM de Instagram',
                      'Respuesta',
                      'Conversación',
                      'Cualificación',
                      'Reserva'
                    ]
                  : [
                      'Instagram DM',
                      'Response',
                      'Conversation',
                      'Qualification',
                      'Booking'
                    ]
                ).map((step, index) => (
                  <div
                    key={step}
                    className="flex items-center gap-4 text-orange-500 font-black"
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-500 text-sm text-white">
                      {index + 1}
                    </span>
                    <span>{step}</span>
                  </div>
                ))}
              </div>

              <p className="text-white font-bold">
                {isSpanish
                  ? 'A partir de ahí, tu equipo toma el relevo.'
                  : 'Your team takes it from there.'}
              </p>
            </div>
          </div>
        </section>

        <AboutStory />

        <section className="mb-28 max-w-4xl">
          <h2 className="text-4xl md:text-5xl font-black tracking-tighter leading-[0.95] mb-8">
            {isSpanish
              ? '¿Dónde se están perdiendo tus clientes potenciales?'
              : 'Where are potential customers getting lost?'}
          </h2>

          <div className="space-y-5 text-zinc-400 text-lg leading-relaxed">
            <p>
              {isSpanish
                ? 'Trabajo con negocios que ya tienen algo que merece la pena vender. No necesitas convertirte en experto en marketing, publicar todos los días ni contratar otra agencia que te envíe un calendario mensual de contenidos.'
                : 'I work with businesses that already have something worth selling. You do not need to become a marketing expert, post every day or hire another agency to send you a monthly content calendar.'}
            </p>

            <p>
              {isSpanish
                ? 'Necesitas a alguien que pueda mirar tu negocio desde fuera y hacer esa pregunta, y después ayudarte a solucionarlo.'
                : 'You need someone who can look at your business from the outside and ask that question, then help you fix it.'}
            </p>

            <p>
              {isSpanish
                ? 'Eso puede significar mejorar tu oferta, crear contenido que genere confianza, configurar un mejor proceso de captación de leads o automatizar el seguimiento que ahora ocurre manualmente — o no ocurre.'
                : 'That might mean sharpening your offer, creating content that builds trust, setting up a better lead capture process or automating the follow-up that currently happens manually — or not at all.'}
            </p>

            <p className="text-2xl text-white font-black pt-3">
              {isSpanish
                ? 'El objetivo no es hacer más marketing. Es conseguir más clientes con la atención que ya estás recibiendo.'
                : 'The goal is not more marketing. The goal is more customers from the attention you are already getting.'}
            </p>
          </div>
        </section>

        <AboutExpertise />

        <motion.section
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="relative overflow-hidden p-10 md:p-16 bg-zinc-900 border-2 border-orange-500/40 rounded-[3rem] text-center"
        >
          <div className="absolute inset-0 bg-orange-500/5 pointer-events-none" />

          <div className="relative z-10 max-w-3xl mx-auto">
            <div className="flex justify-center items-center gap-2 text-orange-500 font-black uppercase tracking-[0.2em] text-xs mb-6">
              <SafeIcon icon={FiMapPin} />
              {isSpanish ? 'Cerca de Barcelona' : 'Based near Barcelona'}
            </div>

            <h2 className="text-4xl md:text-6xl font-black tracking-tighter leading-[0.92] mb-7">
              {isSpanish
                ? '¿Listo para descubrir qué podría funcionar mejor?'
                : 'Ready to see what could work better?'}
            </h2>

            <p className="text-lg md:text-xl text-zinc-400 leading-relaxed mb-10">
              {isSpanish
                ? 'Trabajo principalmente con negocios locales ambiciosos de Barcelona y sus alrededores. Revisaré tu web, tu oferta y tu recorrido de cliente, y te mostraré al menos una cosa que mejoraría.'
                : 'I work primarily with ambitious local businesses in and around Barcelona. I will look at your current website, offer and customer journey, then show you at least one thing I would improve.'}
            </p>

            <a
              href={tidycalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-9 py-5 bg-white text-zinc-950 text-lg font-black rounded-full hover:bg-orange-500 hover:text-white transition-all hover:scale-105 shadow-2xl"
            >
              {isSpanish
                ? 'Reserva una Revisión de Crecimiento gratuita'
                : 'Book a free Growth Review'}
              <SafeIcon icon={FiArrowRight} />
            </a>

            <div className="mt-10 pt-8 border-t border-white/10 text-zinc-500 text-sm">
              <p className="text-white font-black text-base mb-2">
                Mathias Deppe.
              </p>

              <p>
                {isSpanish
                  ? 'Pensamiento comercial. Contenido que genera confianza. Sistemas que convierten la atención en clientes.'
                  : 'Sales thinking. Content that builds trust. Systems that turn attention into customers.'}
              </p>

              <a
                href="mailto:contact@mdlens.com"
                className="inline-flex items-center gap-2 mt-5 text-orange-500 hover:text-orange-400 font-bold transition-colors"
              >
                <SafeIcon icon={FiMail} />
                contact@mdlens.com
              </a>

              <p className="mt-3">Barberà del Vallès, Barcelona</p>
            </div>
          </div>
        </motion.section>
      </main>
    </div>
  );
};

export default About;